/* Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved. */

/**
 * What adding a seat says while the subscription is in its free period.
 *
 * Nothing is charged today (RN-08); the new total is the first charge, on
 * the conversion date (RN-14). The figures are the server's.
 */

import { useTranslation } from 'react-i18next';
import { Text } from '@/shared/ui';
import type { ChangeItemsPreview } from '@/services/requests/housien-services/AgenciesService.team';

/** What the free period caps each seat at (DT-014). */
const TRIAL_AI_CAP = 200;
const TRIAL_SEARCH_CAP = 3;

interface Props {
  preview: ChangeItemsPreview;
  planId: string;
  planTitle: string;
  /** Conversion date, already formatted; null when the subscription has none. */
  date: string | null;
  money: (cents: number) => string;
}

/**
 * The quote of a seat added in the free period.
 *
 * @param props - Component props.
 * @returns The today / first-charge lines and what the new seat gets.
 */
export function AddSeatTrialQuote({ preview, planId, planTitle, date, money }: Props) {
  const { t } = useTranslation('features/subscription');
  const line = preview.items.find((item) => item.plan_id === planId);
  const total = money(preview.next_charge_amount_cents ?? preview.new_total_cents);
  return (
    <div data-testid="add-seat-trial-quote" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
      <Text style={{ fontSize: 'var(--text-body-sm)' }}>
        {t('v2.seats.addModal.trial.chargedToday')}{' '}
        <strong>{money(preview.charged_today_cents)}</strong>
      </Text>
      <Text style={{ fontSize: 'var(--text-body-sm)' }}>
        {date
          ? t('v2.seats.addModal.trial.firstCharge', { date, total })
          : t('v2.seats.addModal.trial.firstChargeNoDate', { total })}
      </Text>
      {line && (
        <Text style={{ fontSize: 'var(--text-small)', color: 'var(--text-secondary)' }}>
          {t('v2.seats.addModal.trial.breakdown', {
            count: line.quantity, plan: planTitle, price: money(line.unit_price_cents),
          })}
        </Text>
      )}
      <Text style={{ fontSize: 'var(--text-small)', color: 'var(--text-secondary)' }}>
        {t('v2.seats.addModal.trial.notice', {
          interactions: TRIAL_AI_CAP, searches: TRIAL_SEARCH_CAP,
        })}
      </Text>
    </div>
  );
}

/**
 * The refusal for a first payment still being confirmed: no figures, no retry.
 *
 * @returns The sentence, as an alert.
 */
export function AddSeatPaymentPending() {
  const { t } = useTranslation('features/subscription');
  return (
    <Text role="alert" data-testid="add-seat-payment-pending" style={{ fontSize: 'var(--text-small)', lineHeight: 1.5 }}>
      {t('v2.seats.addModal.trial.paymentPending')}
    </Text>
  );
}
